import React from "react";
import {
  SCHEMA_BRAND_NAME,
  RETURN_WINDOW_DAYS,
  postageFor,
} from "../lib/productSchema";

/**
 * The merchant-listing fields the shop cards were missing, as microdata.
 *
 * The cards mark themselves up as `schema.org/Product` with a priced
 * `schema.org/Offer`, which is what makes Google read them as merchant listings
 * rather than plain product snippets — so they are held to the same bar as the
 * detail pages and were failing it on four counts (brand, description,
 * shippingDetails, hasMerchantReturnPolicy).
 *
 * Values come from `app/lib/productSchema.ts`, the same module the detail pages'
 * JSON-LD is built from, so a card and the page it links to can never advertise
 * different postage.
 *
 * Deliberately plain `<meta itemProp>` rather than `<link itemProp>`: React 19
 * skips its head-hoisting for any meta/link carrying an `itemProp`, and the
 * `<meta itemProp="availability">` already in these cards is the proof it
 * survives to the rendered HTML. Don't switch these to `<link>` without
 * re-checking the rendered output.
 */

/** Goes directly inside the Product scope. Satisfies "no global identifier" —
 *  these are own-label herbal products with no GTIN, and `brand` is the
 *  alternative Google names. */
export function BrandMicrodata() {
  return (
    <span itemProp="brand" itemScope itemType="https://schema.org/Brand">
      <meta itemProp="name" content={SCHEMA_BRAND_NAME} />
    </span>
  );
}

/** Goes directly inside the Offer scope. `price` is the numeric product price —
 *  postage follows the cart's free-shipping threshold from it. */
export function OfferPoliciesMicrodata({ price }: { price: number | null }) {
  return (
    <>
      <span itemProp="seller" itemScope itemType="https://schema.org/Organization">
        <meta itemProp="name" content={SCHEMA_BRAND_NAME} />
      </span>
      <span
        itemProp="hasMerchantReturnPolicy"
        itemScope
        itemType="https://schema.org/MerchantReturnPolicy"
      >
        <meta itemProp="applicableCountry" content="GB" />
        <meta
          itemProp="returnPolicyCategory"
          content="https://schema.org/MerchantReturnFiniteReturnWindow"
        />
        <meta itemProp="merchantReturnDays" content={String(RETURN_WINDOW_DAYS)} />
        <meta itemProp="returnMethod" content="https://schema.org/ReturnByMail" />
        <meta itemProp="returnFees" content="https://schema.org/FreeReturn" />
      </span>
      <span
        itemProp="shippingDetails"
        itemScope
        itemType="https://schema.org/OfferShippingDetails"
      >
        <span itemProp="shippingRate" itemScope itemType="https://schema.org/MonetaryAmount">
          <meta itemProp="value" content={postageFor(price).toFixed(2)} />
          <meta itemProp="currency" content="GBP" />
        </span>
        <span itemProp="shippingDestination" itemScope itemType="https://schema.org/DefinedRegion">
          <meta itemProp="addressCountry" content="GB" />
        </span>
        <span
          itemProp="deliveryTime"
          itemScope
          itemType="https://schema.org/ShippingDeliveryTime"
        >
          <span itemProp="handlingTime" itemScope itemType="https://schema.org/QuantitativeValue">
            <meta itemProp="minValue" content="0" />
            <meta itemProp="maxValue" content="1" />
            <meta itemProp="unitCode" content="DAY" />
          </span>
          <span itemProp="transitTime" itemScope itemType="https://schema.org/QuantitativeValue">
            <meta itemProp="minValue" content="1" />
            <meta itemProp="maxValue" content="2" />
            <meta itemProp="unitCode" content="DAY" />
          </span>
        </span>
      </span>
    </>
  );
}
